import type { BadgeColor } from 'app/components/ui/badge'

/**
 * Why an AI reply attempt failed, as TRK-5908 records it. Intentional non-replies (opt-out,
 * quiet hours, staff takeover, burst dedupe) are never recorded, so every reason here is a
 * real failure.
 */
export const FAILURE_REASON_LABEL: Record<string, string> = {
  no_agent_configuration: `No AI agent set up`,
  no_prompt_deployed: `Prompt not deployed`,
  empty_prompt: `Empty prompt`,
  template_validation: `Prompt template error`,
  llm_error: `AI model error`,
  send_error: `Text failed to send`,
  unknown_error: `Unknown error`,
}

/** Unanswered: no AI reply on the thread since the last failed attempt. Answered: one came later. */
export type FailedReplyStatus = `unanswered` | `answered`

export const FAILED_REPLY_STATUSES: FailedReplyStatus[] = [`unanswered`, `answered`]

export const FAILED_REPLY_LABEL: Record<FailedReplyStatus, string> = {
  unanswered: `Not answered`,
  answered: `Answered later`,
}

export const FAILED_REPLY_COLOR: Record<FailedReplyStatus, BadgeColor> = {
  unanswered: `red`,
  answered: `green`,
}

/** One patient text the AI failed to answer, with every failed attempt at it folded in. */
export type FailedReply = {
  status: FailedReplyStatus
  /** The patient text the attempts were answering; retries of one text share it. */
  messageId?: string
  threadId: string
  orgId: string
  workspaceId: string
  locationId: string
  contactId?: string
  contactName?: string
  firstFailedAt: string
  lastFailedAt: string
  attempts: number
  /** From the latest attempt, the one that decided the outcome. */
  reason?: string
  failureMessage?: string
  /** The first successful AI reply on the thread after the last failed attempt. */
  answeredAt?: string
  answeredAfterMinutes?: number
}

type Attempt = {
  id: string
  orgId: string
  workspaceId: string
  locationId: string
  threadId?: string | null
  contactId?: string | null
  messageId?: string | null
  failed?: boolean | null
  failureReason?: string | null
  failureMessage?: string | null
  createdDate: string
}

/**
 * Failed AI reply attempts grouped into one row per patient text, unanswered first, then newest
 * first (plan D39). A failed reply rethrows so the queue retries it, and each retry that fails
 * writes its own row, so attempts sharing a message id are one text. A text counts as answered
 * when any successful attempt lands on its thread after its last failure.
 */
export function failedReplies(failedAttempts: Attempt[], laterAttempts: Attempt[]): FailedReply[] {
  const byText = new Map<string, Attempt[]>()
  for (const attempt of failedAttempts) {
    if (!attempt.failed || !attempt.threadId) continue
    const key = attempt.messageId ?? `${attempt.threadId}:${attempt.id}`
    byText.set(key, [...(byText.get(key) ?? []), attempt])
  }

  const successTimesByThread = new Map<string, number[]>()
  for (const attempt of laterAttempts) {
    if (attempt.failed || !attempt.threadId) continue
    successTimesByThread.set(attempt.threadId, [
      ...(successTimesByThread.get(attempt.threadId) ?? []),
      Date.parse(attempt.createdDate),
    ])
  }

  const rows = [...byText.values()].map((attempts): FailedReply => {
    const sorted = [...attempts].sort((a, b) => Date.parse(a.createdDate) - Date.parse(b.createdDate))
    const first = sorted[0]
    const last = sorted[sorted.length - 1]
    const lastFailed = Date.parse(last.createdDate)
    const answered = (successTimesByThread.get(last.threadId!) ?? [])
      .filter((time) => time > lastFailed)
      .sort((a, b) => a - b)[0]
    return {
      status: answered === undefined ? `unanswered` : `answered`,
      messageId: last.messageId ?? undefined,
      threadId: last.threadId!,
      orgId: last.orgId,
      workspaceId: last.workspaceId,
      locationId: last.locationId,
      contactId: sorted.find((attempt) => attempt.contactId)?.contactId ?? undefined,
      firstFailedAt: first.createdDate,
      lastFailedAt: last.createdDate,
      attempts: sorted.length,
      reason: last.failureReason ?? undefined,
      failureMessage: last.failureMessage ?? undefined,
      answeredAt: answered === undefined ? undefined : new Date(answered).toISOString(),
      answeredAfterMinutes: answered === undefined ? undefined : (answered - lastFailed) / 60_000,
    }
  })

  return rows.sort(
    (a, b) =>
      FAILED_REPLY_STATUSES.indexOf(a.status) - FAILED_REPLY_STATUSES.indexOf(b.status) ||
      Date.parse(b.lastFailedAt) - Date.parse(a.lastFailedAt),
  )
}
